// Sesión + tenant activo. El panel es multi-tenant POR PERTENENCIA (C0-D2):
// el usuario puede ser miembro de varios negocios y aquí se elige cuál se está
// operando. La barrera real es la RLS; este selector es solo el filtro.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { sb } from "./supabase.ts";

export interface Membresia { tenant_id: string; rol: "dueno" | "agente"; nombre: string; slug: string | null }

interface Sesion {
  cargando: boolean;
  usuarioId: string | null;
  correo: string | null;
  membresias: Membresia[];
  tenantId: string | null;
  rol: "dueno" | "agente" | "victorix";
  /**
   * ⭐ El `access_token` VIVO de la sesión, para llamar a APIs propias de
   * Victorix que no son PostgREST — hoy solo el simulador («Probar bot»,
   * T-SIM-16). Es una función y no un campo a propósito: `getSession()` renueva
   * el token si hace falta, mientras que uno guardado en el estado estaría
   * vencido a la hora y el usuario vería un 401 sin entender por qué.
   * ⛔ Este token NO se manda a nadie más que a nuestro propio backend.
   */
  obtenerToken: () => Promise<string | null>;
  elegirTenant: (id: string) => void;
  salir: () => Promise<void>;
}

const Ctx = createContext<Sesion | null>(null);
const LLAVE_TENANT = "victorix-chat-ai:tenant";

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [cargando, setCargando] = useState(true);
  const [usuarioId, setUsuarioId] = useState<string | null>(null);
  const [correo, setCorreo] = useState<string | null>(null);
  const [membresias, setMembresias] = useState<Membresia[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [esVictorix, setEsVictorix] = useState(false);

  async function cargarMembresias(uid: string | null) {
    if (!uid) { setMembresias([]); setTenantId(null); return; }
    const { data } = await sb.from("miembros")
      .select("tenant_id, rol, tenants(nombre, slug)").eq("user_id", uid).eq("activo", true);
    const ms: Membresia[] = (data ?? []).map((m: any) => ({
      tenant_id: m.tenant_id, rol: m.rol, nombre: m.tenants?.nombre ?? "Negocio",
      slug: m.tenants?.slug ?? null,
    }));
    setMembresias(ms);
    const guardado = localStorage.getItem(LLAVE_TENANT);
    const elegido = ms.find((m) => m.tenant_id === guardado)?.tenant_id ?? ms[0]?.tenant_id ?? null;
    setTenantId(elegido);
  }

  useEffect(() => {
    sb.auth.getSession().then(async ({ data }) => {
      const u = data.session?.user ?? null;
      setUsuarioId(u?.id ?? null);
      setCorreo(u?.email ?? null);
      setEsVictorix(Boolean((u?.app_metadata as any)?.es_victorix));
      await cargarMembresias(u?.id ?? null);
      setCargando(false);
    });
    const { data: sub } = sb.auth.onAuthStateChange(async (_e, session) => {
      const u = session?.user ?? null;
      setUsuarioId(u?.id ?? null);
      setCorreo(u?.email ?? null);
      setEsVictorix(Boolean((u?.app_metadata as any)?.es_victorix));
      await cargarMembresias(u?.id ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const valor = useMemo<Sesion>(() => ({
    cargando, usuarioId, correo, membresias, tenantId,
    rol: esVictorix ? "victorix" : (membresias.find((m) => m.tenant_id === tenantId)?.rol ?? "agente"),
    obtenerToken: async () => (await sb.auth.getSession()).data.session?.access_token ?? null,
    elegirTenant: (id) => { localStorage.setItem(LLAVE_TENANT, id); setTenantId(id); },
    salir: async () => { await sb.auth.signOut(); localStorage.removeItem(LLAVE_TENANT); },
  }), [cargando, usuarioId, correo, membresias, tenantId, esVictorix]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion(): Sesion {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSesion fuera del ProveedorSesion");
  return c;
}
